import {
  PROPOSAL_BUNDLE_CATEGORY,
  TICKET_TYPE_FOREIGN,
  type ProposalBundle,
  type ProposalItem
} from '../../types/proposalBundles';
import { formatRouteDate } from '../../utils/routeInfoFormatter';
import { formatDurationMinutesJa, minutesBetweenDepartureArrival } from '../../utils/flightTimeDuration';
import { FLIGHT_CODE_PATTERN } from '../../utils/flightCodePattern';

export type FlightEndpointDisplay = {
  time: string;
  dateLabel: string | null;
};

export type ForeignFlightTimeRangeDisplay = {
  departure: FlightEndpointDisplay | null;
  arrival: FlightEndpointDisplay | null;
};

const buildFlightEndpointDisplay = (time: string | null, dateIso: string | null): FlightEndpointDisplay | null => {
  if (time == null || time.length === 0) {
    return null;
  }
  return {
    time,
    dateLabel: formatRouteDate(dateIso)
  };
};

export const buildFlightTimeRangeDisplay = (
  departureTime: string | null,
  arrivalTime: string | null,
  departureDate: string | null,
  arrivalDate: string | null
): ForeignFlightTimeRangeDisplay => ({
  departure: buildFlightEndpointDisplay(departureTime, departureDate),
  arrival: buildFlightEndpointDisplay(arrivalTime, arrivalDate)
});

export type ForeignFlightRowDisplay = {
  timeRangeLabel: string;
  timeRangeDisplay: ForeignFlightTimeRangeDisplay;
  durationMain: string | null;
  durationSub: string | null;
  segmentLabel: string;
  airlineCarrier: string;
  flightMain: string;
  ticketExtra: string | null;
  seatLabel: string;
};

type ForeignFlightDurationLabels = Pick<ForeignFlightRowDisplay, 'durationMain' | 'durationSub'>;
type ForeignFlightAirlineFlightLabels = Pick<
  ForeignFlightRowDisplay,
  'airlineCarrier' | 'flightMain' | 'ticketExtra'
>;

const EMPTY_LABEL = '—';
const MISSING_FLIGHT_LABEL = '-';

const FLIGHT_CODE_WITH_CARRIER_PATTERN = new RegExp(
  `^(.+?)\\s+(${FLIGHT_CODE_PATTERN.source})$`,
  FLIGHT_CODE_PATTERN.flags
);

const emptyToNull = (value: string | null | undefined): string | null =>
  value != null && value.length > 0 ? value : null;

const blankToNull = (value: string | null | undefined): string | null => {
  if (value == null) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const isPriceLikeText = (text: string): boolean => {
  const trimmed = text.trim();
  if (/^[\d,]+円$/.test(trimmed)) {
    return true;
  }
  if (/^¥\s*[\d,]+$/.test(trimmed)) {
    return true;
  }
  if (/^[\d,]+\s*円（税込）?$/.test(trimmed)) {
    return true;
  }
  return false;
};

const splitCarrierAndFlightCode = (rawValue: string): { carrier: string | null; code: string } => {
  const raw = rawValue.trim();
  if (!raw) {
    return { carrier: null, code: MISSING_FLIGHT_LABEL };
  }
  const match = raw.match(FLIGHT_CODE_WITH_CARRIER_PATTERN);
  if (match) {
    return { carrier: match[1].trim(), code: match[2].trim() };
  }
  return { carrier: null, code: raw };
};

const buildTimeRangeLabel = (item: ProposalItem): string => {
  if (item.departureTime == null || item.arrivalTime == null) {
    return EMPTY_LABEL;
  }
  return `${item.departureTime} → ${item.arrivalTime}`;
};

const buildOneWayDurationFromTimes = (item: ProposalItem): string | null => {
  const segmentMinutes = minutesBetweenDepartureArrival(item.departureTime, item.arrivalTime);
  if (segmentMinutes == null || segmentMinutes <= 0) {
    return null;
  }
  return `${formatDurationMinutesJa(segmentMinutes)}（片道）`;
};

const buildDurationLabels = (item: ProposalItem, hasRoundTripPair: boolean): ForeignFlightDurationLabels => {
  const roundTripLabel = emptyToNull(item.roundTripDurationLabel);
  const legSummary = emptyToNull(item.legDurationSummary);
  const oneWayFromTimes = hasRoundTripPair ? null : buildOneWayDurationFromTimes(item);
  const durationMain = roundTripLabel ?? legSummary ?? oneWayFromTimes;
  const durationSub =
    roundTripLabel != null && legSummary != null && legSummary !== roundTripLabel ? legSummary : null;
  return { durationMain, durationSub };
};

const buildSegmentLabel = (item: ProposalItem): string => {
  const segmentRouteLabel = emptyToNull(item.segmentRouteLabel);
  if (segmentRouteLabel != null) {
    return segmentRouteLabel;
  }
  const subtitle = emptyToNull(item.subtitle);
  if (subtitle != null && !isPriceLikeText(subtitle)) {
    return subtitle;
  }
  const connectionSummary = emptyToNull(item.connectionSummary);
  if (connectionSummary != null) {
    return connectionSummary;
  }
  return EMPTY_LABEL;
};

const buildAirlineAndFlightLabels = (item: ProposalItem): ForeignFlightAirlineFlightLabels => {
  const airlineDisplay = blankToNull(item.airlineDisplay) ?? '';
  const flightNameRaw = blankToNull(item.flightName ?? item.title ?? '') ?? '';
  const splitCarrierAndCode = splitCarrierAndFlightCode(flightNameRaw);
  const airlineCarrier = airlineDisplay.length > 0 ? airlineDisplay : splitCarrierAndCode.carrier ?? '';
  let flightMain = flightNameRaw.length > 0 ? flightNameRaw : MISSING_FLIGHT_LABEL;
  if (airlineDisplay.length > 0 && flightMain.startsWith(`${airlineDisplay} `)) {
    flightMain = flightMain.slice(airlineDisplay.length).trim() || flightMain;
  } else if (airlineDisplay.length === 0 && splitCarrierAndCode.carrier != null) {
    flightMain = splitCarrierAndCode.code;
  }
  const ticketTypeRaw = blankToNull(item.ticketType);
  const ticketTypeForDisplay = ticketTypeRaw === TICKET_TYPE_FOREIGN ? null : ticketTypeRaw;
  const ticketExtra =
    ticketTypeForDisplay != null && item.ticketCategory != null && ticketTypeForDisplay === item.ticketCategory
      ? null
      : ticketTypeForDisplay;
  return { airlineCarrier, flightMain, ticketExtra };
};

const buildSeatLabel = (item: ProposalItem): string => blankToNull(item.seatClass) ?? EMPTY_LABEL;

export const buildForeignFlightRowDisplay = (
  item: ProposalItem,
  hasRoundTripPair: boolean
): ForeignFlightRowDisplay => ({
  timeRangeLabel: buildTimeRangeLabel(item),
  timeRangeDisplay: buildFlightTimeRangeDisplay(
    item.departureTime,
    item.arrivalTime,
    item.departureDate,
    item.arrivalDate
  ),
  ...buildDurationLabels(item, hasRoundTripPair),
  segmentLabel: buildSegmentLabel(item),
  ...buildAirlineAndFlightLabels(item),
  seatLabel: buildSeatLabel(item)
});

type ForeignFlightScope = 'single' | 'outward' | 'homeward' | 'roundTrip';

const DURATION_HEADER_LABELS: Record<ForeignFlightScope, string> = {
  single: '所要時間',
  outward: '所要時間（往路）',
  homeward: '所要時間（復路）',
  roundTrip: '所要時間（往復）'
};

const CAPTION_HINT_LABELS: Record<ForeignFlightScope, string> = {
  single: '表は当該片道の区間・所要時間です。乗継がある場合は区間表記と所要時間をご確認ください。',
  outward: '表は往路の区間・所要時間です。料金は往復セットのまま表示されます。',
  homeward: '表は復路の区間・所要時間です。料金は往復セットのまま表示されます。',
  roundTrip: '往路・復路をまとめた所要時間の目安です。区間は便により異なります。'
};

const resolveForeignFlightScope = (
  hasRoundTripPair: boolean,
  bundleCategory: ProposalBundle['category']
): ForeignFlightScope => {
  if (!hasRoundTripPair) {
    return 'single';
  }
  if (bundleCategory === PROPOSAL_BUNDLE_CATEGORY.OUTWARD) {
    return 'outward';
  }
  if (bundleCategory === PROPOSAL_BUNDLE_CATEGORY.HOMEWARD) {
    return 'homeward';
  }
  return 'roundTrip';
};

export const getForeignFlightDurationHeaderLabel = (
  hasRoundTripPair: boolean,
  bundleCategory: ProposalBundle['category']
): string => DURATION_HEADER_LABELS[resolveForeignFlightScope(hasRoundTripPair, bundleCategory)];

export const getForeignFlightCaptionHint = (
  hasRoundTripPair: boolean,
  bundleCategory: ProposalBundle['category']
): string => CAPTION_HINT_LABELS[resolveForeignFlightScope(hasRoundTripPair, bundleCategory)];
